import { authApi } from 'api';

const safeMode = false; // Set to true for safe mode, false for actual API calls

export const importCsv = async (clubId, file) => {
  const formData = new FormData();
  formData.append('clubId', clubId);
  formData.append('file', file);

  if (safeMode) {
    console.log('SAFE MODE - importCsv API call would be:', {
      method: 'POST',
      url: '/MemberImport/import-csv',
      body: { clubId, fileName: file.name, fileSize: file.size },
    });
    // Return mock response for safe mode
    return {
      data: {
        message: 'Import job queued successfully (SAFE MODE)',
        importJobId: 'mock-job-id-' + Date.now(),
        totalRecords: 10,
        estimatedDurationMinutes: 1,
        queuePosition: 1,
      },
    };
  }

  return await authApi.post('/MemberImport/import-csv', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Dry-run preview for the member import: parse + resolve, NO writes. Returns the per-row resolution
// (Update / Link / Create / Skip / Block + reason + CSV-vs-portal diffs). dateFormat optionally overrides
// slash-date interpretation ('dayfirst' | 'monthfirst'); omit to auto-detect.
export const previewCsv = async (clubId, file, dateFormat = null) => {
  const formData = new FormData();
  formData.append('clubId', clubId);
  formData.append('file', file);
  if (dateFormat) formData.append('dateFormat', dateFormat);

  return await authApi.post('/MemberImport/preview-csv', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// APPLY the member import (writes). Re-resolves the same file server-side (no stored plan), executes the plan, and
// returns a preview-shaped result report (per-row outcome + the same diffs the preview showed). dateFormat optionally
// overrides slash-date interpretation, same as previewCsv.
export const applyCsv = async (clubId, file, dateFormat = null) => {
  const formData = new FormData();
  formData.append('clubId', clubId);
  formData.append('file', file);
  if (dateFormat) formData.append('dateFormat', dateFormat);

  return await authApi.post('/MemberImport/apply-csv', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getImportStatus = async (importJobId) => {
  if (safeMode) {
    console.log('SAFE MODE - getImportStatus API call would be:', {
      method: 'GET',
      url: `/MemberImport/status/${importJobId}`,
    });
    // Return mock completed status for safe mode
    return {
      data: {
        importJobId,
        status: 'Completed',
        progress: {
          totalRecords: 10,
          processedRecords: 10,
          successCount: 8,
          updatedCount: 2,
          upToDateCount: 6,
          errorCount: 2,
          currentBatch: 1,
          totalBatches: 1,
          percentComplete: 100,
          queuePosition: 0,
        },
        timestamps: {
          createdAt: new Date().toISOString(),
          startedAt: new Date().toISOString(),
          completedAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          duration: '00:00:05',
        },
        completionMessage: 'Import completed with some errors (SAFE MODE)',
        failureReason: null,
        hasErrors: true,
        originalFileName: 'test-import.csv',
        fileSizeBytes: 1024,
        importType: 'LAQMembers',
        initiatedByUserName: 'Test User',
      },
    };
  }

  return await authApi.get(`/MemberImport/status/${importJobId}`);
};

export const getImportErrors = async (importJobId, page = 1, pageSize = 50) => {
  if (safeMode) {
    console.log('SAFE MODE - getImportErrors API call would be:', {
      method: 'GET',
      url: `/MemberImport/errors/${importJobId}?page=${page}&pageSize=${pageSize}`,
    });
    // Return mock errors for safe mode
    return {
      data: {
        importJobId,
        errors: [
          {
            importJobErrorId: 'mock-error-1',
            recordType: 'Athlete',
            recordIdentifier: 'John Doe (Club ID: 12345)',
            errorMessage: 'Invalid email format',
            requiredAction: 'Correct the email format and re-import',
            csvRowNumber: 1,
            batchNumber: 1,
            createdAt: new Date().toISOString(),
          },
          {
            importJobErrorId: 'mock-error-2',
            recordType: 'Athlete',
            recordIdentifier: 'Jane Smith (Club ID: 12346)',
            errorMessage: 'Birth date is required',
            requiredAction: 'Add birth date and re-import',
            csvRowNumber: 2,
            batchNumber: 1,
            createdAt: new Date().toISOString(),
          },
        ],
        pagination: {
          page: 1,
          pageSize: 50,
          totalErrors: 2,
          totalPages: 1,
        },
      },
    };
  }

  return await authApi.get(
    `/MemberImport/errors/${importJobId}?page=${page}&pageSize=${pageSize}`
  );
};

export const cancelImport = async (importJobId) => {
  if (safeMode) {
    console.log('SAFE MODE - cancelImport API call would be:', {
      method: 'POST',
      url: `/MemberImport/cancel/${importJobId}`,
    });
    return {
      data: {
        message: 'Import cancelled successfully (SAFE MODE)',
        importJobId,
      },
    };
  }

  return await authApi.post(`/MemberImport/cancel/${importJobId}`);
};

export const clearImportHistory = async (clubId, type = 0) => {
  if (safeMode) {
    console.log('SAFE MODE - clearImportHistory API call would be:', {
      method: 'DELETE',
      url: `/MemberImport/clear/${clubId}?type=${type}`,
    });
    return {
      data: {
        message: 'Import history cleared successfully (SAFE MODE)',
        clearedJobCount: 1,
      },
    };
  }

  return await authApi.delete(`/MemberImport/clear/${clubId}?type=${type}`);
};

export const getLatestImport = async (clubId, type = 0) => {
  if (safeMode) {
    console.log('SAFE MODE - getLatestImport API call would be:', {
      method: 'GET',
      url: `/MemberImport/latest/${clubId}?type=${type}`,
    });
    return {
      data: null, // No previous import in safe mode
    };
  }

  return await authApi.get(`/MemberImport/latest/${clubId}?type=${type}`);
};
